import { CreditCard } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import { usesTransferWizardCardLayout } from "../../../utils/transferPropertyCompanyWizard"
import type { TransferPropertyInfoSectionProps } from "./propertyInfoSectionTypes"
import { TransferPaymentField } from "./TransferPaymentField"

export function TransferPropertyStandardPaymentsSection({ ctx }: TransferPropertyInfoSectionProps) {
  const {
    fragment,
    propertyData,
    states,
    register,
    paymentMethod,
    payeeName,
  } = ctx
  const propertyStaff = (propertyData?.property_staff as Record<string, unknown>[] | undefined) ?? []
  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const transferPaymentRowClass = useCardLayout ? "grid grid-cols-1 gap-6 md:grid-cols-3" : companyFormStyles.formRow

  const paymentsFields = (
    <>
      <div data-testid="transfer-process-payments-primary-row" className={transferPaymentRowClass}>
        <TransferPaymentField useCardLayout={useCardLayout}>
          <label
            data-testid="transfer-process-payments-method-label"
            className={companyFormStyles.formLabel}
          >
            Method
          </label>
          <select
            data-testid="transfer-process-payments-method-select"
            className={companyFormStyles.customFormControl}
            {...register("payment_method")}
          >
            <option value="">Select Type</option>
            <option value="ACH">ACH</option>
            <option value="PAPER_CHECK">Paper</option>
          </select>
        </TransferPaymentField>
        <TransferPaymentField useCardLayout={useCardLayout}>
          <label
            data-testid="transfer-process-payments-verify-label"
            className={companyFormStyles.formLabel}
          >
            Verified
          </label>
          <select
            data-testid="transfer-process-payments-verify-select"
            className={companyFormStyles.customFormControl}
            {...register("verify")}
          >
            <option value="">Type</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        </TransferPaymentField>
        <TransferPaymentField useCardLayout={useCardLayout}>
          <label
            data-testid="transfer-process-payments-pay-to-label"
            className={companyFormStyles.formLabel}
          >
            Pay To
          </label>
          <select
            data-testid="transfer-process-payments-pay-to-select"
            className={companyFormStyles.customFormControl}
            {...register("pay_to")}
          >
            <option value="">Select</option>
            <option value="COMPANY">Company</option>
            <option value="PROPERTY">Property</option>
          </select>
        </TransferPaymentField>
      </div>
      {paymentMethod === "ACH" && (
        <div data-testid="transfer-process-payments-ach-row" className={transferPaymentRowClass}>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label
              data-testid="transfer-process-payments-routing-label"
              className={companyFormStyles.formLabel}
            >
              ACH Routing
            </label>
            <input
              type="text"
              data-testid="transfer-process-payments-routing-input"
              className={companyFormStyles.customFormControl}
              placeholder="Routing Number"
              {...register("routing_number")}
            />
          </TransferPaymentField>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label
              data-testid="transfer-process-payments-account-label"
              className={companyFormStyles.formLabel}
            >
              ACH Account
            </label>
            <input
              type="text"
              data-testid="transfer-process-payments-account-input"
              className={companyFormStyles.customFormControl}
              placeholder="Account Number"
              {...register("account_number")}
            />
          </TransferPaymentField>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label
              data-testid="transfer-process-payments-account-type-label"
              className={companyFormStyles.formLabel}
            >
              Account Type
            </label>
            <select
              data-testid="transfer-process-payments-account-type-select"
              className={companyFormStyles.customFormControl}
              {...register("property_method")}
            >
              <option value="">Account Type</option>
              <option value="Checkings">Checkings</option>
              <option value="Savings">Savings</option>
            </select>
          </TransferPaymentField>
        </div>
      )}
      {paymentMethod === "PAPER_CHECK" && (
        <>
          <div
            data-testid="transfer-process-payments-paper-row-1"
            className={useCardLayout ? "grid grid-cols-1 gap-6 md:grid-cols-2" : companyFormStyles.formRow}
          >
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label
                data-testid="transfer-process-payments-payee-label"
                className={companyFormStyles.formLabel}
              >
                ATTN Payee
              </label>
              <select
                data-testid="transfer-process-payments-payee-select"
                className={companyFormStyles.customFormControl}
                {...register("payee_name")}
              >
                <option value="">Select ATTN Contact</option>
                <option value="LEASING">Leasing Office</option>
                {propertyStaff.map((p) => (
                  <option key={String(p.user_id)} value={String(p.user_id)}>
                    {String(p.first_name ?? "")} {String(p.last_name ?? "")}
                  </option>
                ))}
                <option value="OTHER">Other</option>
              </select>
              {payeeName === "OTHER" && (
                <input
                  type="text"
                  data-testid="transfer-process-payments-other-payee-input"
                  className={cn(companyFormStyles.customFormControl, "mt-2")}
                  {...register("other_payee_name")}
                />
              )}
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label
                data-testid="transfer-process-payments-address-label"
                className={companyFormStyles.formLabel}
                htmlFor="transfer-payment-address"
              >
                Street Address
              </label>
              <textarea
                id="transfer-payment-address"
                data-testid="transfer-process-payments-address-textarea"
                rows={2}
                className={cn(companyFormStyles.customFormControl, "min-h-[72px] resize-y")}
                placeholder=""
                {...register("payment_address")}
              />
            </TransferPaymentField>
          </div>
          <div
            data-testid="transfer-process-payments-paper-row-2"
            className={
              useCardLayout
                ? "grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
                : companyFormStyles.formRow
            }
          >
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label
                data-testid="transfer-process-payments-suite-label"
                className={companyFormStyles.formLabel}
              >
                Suite/Shop
              </label>
              <input
                type="text"
                data-testid="transfer-process-payments-suite-input"
                className={companyFormStyles.customFormControl}
                placeholder="Suite/Shop"
                {...register("payment_shop")}
              />
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label
                data-testid="transfer-process-payments-city-label"
                className={companyFormStyles.formLabel}
              >
                City
              </label>
              <input
                type="text"
                data-testid="transfer-process-payments-city-input"
                className={companyFormStyles.customFormControl}
                placeholder="Payment City"
                {...register("payment_city")}
              />
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label
                data-testid="transfer-process-payments-state-label"
                className={companyFormStyles.formLabel}
              >
                State
              </label>
              <select
                data-testid="transfer-process-payments-state-select"
                className={companyFormStyles.customFormControl}
                {...register("payment_state")}
              >
                <option value="">State</option>
                {states.map((s) => (
                  <option key={`pay-${s.state_code}`} value={s.state_code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label
                data-testid="transfer-process-payments-zipcode-label"
                className={companyFormStyles.formLabel}
              >
                Zipcode
              </label>
              <input
                type="text"
                data-testid="transfer-process-payments-zipcode-input"
                className={companyFormStyles.customFormControl}
                placeholder="Payment Zipcode"
                {...register("payment_zipcode")}
              />
            </TransferPaymentField>
          </div>
        </>
      )}
    </>
  )

  if (useCardLayout) {
    return (
      <SectionCard
        icon={CreditCard}
        title="Payments Preferences"
        testId="transfer-process-payments-section"
        titleTestId="transfer-process-payments-section-title"
      >
        <div className="space-y-5">{paymentsFields}</div>
      </SectionCard>
    )
  }

  return (
    <>
      <h2
        data-testid="transfer-process-payments-section-title"
        className={companyFormStyles.sectionTitle}
      >
        Payments Preferences
      </h2>
      {paymentsFields}
    </>
  )
}
